import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { setConsent } from "@/lib/consent";
import { clearAttribution } from "@/lib/analytics";
import { useToast } from "@/hooks/use-toast";

// Keep this list of data and processors in sync with what the app actually
// collects (Supabase, Stripe, Google sign-in) whenever that changes. Quizrun
// collects personal data across several jurisdictions (UK/EU under UK GDPR &
// EU GDPR, plus US events), so wording is kept general enough to hold across
// those regimes.

const PRIVACY_VERSION = "2026-07-22";

const Privacy = () => {
  const { toast } = useToast();

  useEffect(() => {
    document.title = "Privacy Policy — Quizrun";
  }, []);

  // Let people withdraw analytics consent after the banner is gone. Rejecting
  // here stops future tracker loads; already-loaded tags clear on next page load.
  const withdrawAnalytics = () => {
    setConsent("rejected");
    clearAttribution();
    toast({
      title: "Analytics cookies turned off",
      description: "We won't load Google Analytics or the Meta Pixel anymore.",
    });
  };

  return (
    <main>
      <PageHeader
        eyebrow="Legal"
        title="Privacy Policy"
        intro={
          <>
            How Quizrun collects, uses and protects your personal data. Please
            read this alongside our{" "}
            <Link
              to="/terms"
              className="underline underline-offset-2 hover:opacity-100"
            >
              Terms &amp; Waiver
            </Link>
            .
          </>
        }
      />

      <section className="container mx-auto px-4 md:px-8 pb-16">
        <div className="max-w-2xl space-y-6 text-sm leading-relaxed text-muted-foreground">
          <div className="text-xs text-muted-foreground">
            Version {PRIVACY_VERSION}
          </div>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              1. Who we are
            </h2>
            <p>
              Quizrun is run by its organisers, who are currently the joint
              controllers of the personal data described here, acting as
              individuals rather than through a registered company. For any
              privacy question, or to reach the organisers, contact us
              at{" "}
              <a
                href="mailto:quizrun.org@gmail.com"
                className="underline underline-offset-2 hover:text-foreground"
              >
                quizrun.org@gmail.com
              </a>
              .
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              2. What we collect
            </h2>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Identity &amp; contact: your name, email address
                and, optionally, phone number.
              </li>
              <li>
                Profile details you choose to add: gender and home city/country.
              </li>
              <li>
                Event data: which events you register for and take part in, your
                team, and your results.
              </li>
              <li>
                Payment data: when you pay for a ticket or make a donation.
                Card details are handled by our payment provider — we do not
                store your full card number.
              </li>
              <li>
                Records of your acceptance of our Terms &amp; Waiver (the date,
                the version, and your health self-certification).
              </li>
              <li>
                Account data: if you sign in with Google, we receive your name,
                email address and basic Google profile information.
              </li>
              <li>
                Technical data such as basic logs when you use the website.
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              3. Why we use it &amp; our lawful basis
            </h2>
            <p>
              We use your data to register you for events, run the
              events safely (including holding a phone number for safety on the
              course), process payments, keep results and history, communicate
              about events you join, and improve Quizrun. Depending on the
              purpose, our lawful basis is performance of a contract with you,
              your consent, our legitimate interests in running and promoting
              Quizrun, or compliance with a legal obligation.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              4. Who we share it with
            </h2>
            <p>
              We use trusted service providers who process data on
              our behalf, including our database/hosting provider (Supabase),
              our payment provider (Stripe), and Google (for optional
              sign-in). Local event hosts may see the participant list for their
              event. We do not sell your personal data.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              5. International transfers
            </h2>
            <p>
              Quizrun operates in several countries and some of our
              providers are based outside your country. Where personal data is
              transferred internationally, we rely on appropriate safeguards
              recognised under applicable data-protection law.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              6. Cookies &amp; analytics
            </h2>
            <p>
              We use the cookies and similar storage needed to make the site
              work and keep you signed in. These are essential and always on.
            </p>
            <p>
              With your consent, we also load two optional third-party tools to
              measure how our Instagram/Facebook campaigns perform:{" "}
              <span className="font-medium text-foreground">
                Google Analytics 4
              </span>{" "}
              (counts how many visitors complete a registration and which
              campaign they came from) and the{" "}
              <span className="font-medium text-foreground">Meta Pixel</span>{" "}
              (lets Meta optimise ad delivery and report on the same
              conversion). We also store the first-touch campaign parameters
              (the <code>utm_*</code> tags from an ad link) in your browser so a
              conversion can be attributed to the right campaign. None of this
              loads or is stored until you accept the cookie banner, and you can
              change your mind at any time:
            </p>
            <Button variant="outline" size="sm" onClick={withdrawAnalytics}>
              Turn off analytics cookies
            </Button>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              7. How we keep it secure
            </h2>
            <p>
              We take reasonable technical and organisational measures to protect
              your data, and our providers (such as Supabase and Stripe) hold
              recognised security standards. No system is completely secure, but
              if a breach affects your rights we will act as the law requires.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              8. How long we keep it
            </h2>
            <p>
              We keep your data for as long as needed for the
              purposes above — for example, to maintain your event history and
              results, meet legal and accounting obligations, and demonstrate
              acceptance of our terms — and then delete or anonymise it.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">9. Your rights</h2>
            <p>
              Depending on where you live, you may have rights to
              access, correct, delete or export your data, to object to or
              restrict certain processing, and to withdraw consent. To exercise
              any of these, email{" "}
              <a
                href="mailto:quizrun.org@gmail.com"
                className="underline underline-offset-2 hover:text-foreground"
              >
                quizrun.org@gmail.com
              </a>
              . You may also complain to your local data-protection authority
              (in the UK, the Information Commissioner&apos;s Office).
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-bold text-foreground">
              10. Children
            </h2>
            <p>
              Quizrun is intended for adults, and where a participant is under
              18 we expect a parent or guardian to register on their behalf and
              to consent to this policy. The minimum age at which a person can
              consent to their own data being processed varies by country; we do
              not knowingly collect a child&apos;s data below that age without
              parental consent. See also the Age &amp; minors section of our
              Terms.
            </p>
          </section>

          <p className="text-xs">
            Questions? Email{" "}
            <a
              href="mailto:quizrun.org@gmail.com"
              className="underline underline-offset-2 hover:text-foreground"
            >
              quizrun.org@gmail.com
            </a>
            .
          </p>
        </div>
      </section>
    </main>
  );
};

export default Privacy;
